import { useEffect, useRef, useState } from "react";
import { PartnerOnlinePricingPreview } from "../../../businessesApi";
import {
  loadOnlinePricingPreview,
  peekOnlinePricingPreview,
} from "./pricingPrefetch";

export type OnlinePricingPreviewState = {
  loading: boolean;
  preview?: PartnerOnlinePricingPreview;
  // The request itself failed (network / 5xx), as opposed to succeeding with a
  // non-ok status. The two need different copy: one is worth retrying.
  failed: boolean;
  // What it failed with, so the pane can name it rather than leaving whoever
  // hits it to open devtools.
  failureReason?: string;
};

// Error bodies on these endpoints are the backend's serialized error
// (`{ message, code }`); a request that never got an answer has neither.
function failureReason(error: unknown): string | undefined {
  const failure = error as {
    message?: string;
    response?: { status?: number; data?: { message?: string; code?: string } };
  };
  const body = failure?.response?.data;
  const detail = body?.message || body?.code || failure?.message;
  const status = failure?.response?.status;
  if (!status) return detail;
  return detail ? `${status} · ${detail}` : String(status);
}

function previewParams(
  businessId: string,
  partnerId?: string,
  partnerOnlinePurchPriceProfileId?: string
) {
  return { businessId, partnerId, partnerOnlinePurchPriceProfileId };
}

/** Partner bands when mapped; omit `partnerId` for the unmapped 16-bucket
 *  coverage snapshot. `partnerOnlinePurchPriceProfileId` is the pane's current
 *  range and is omitted when no partner is mapped. */
export function useOnlinePricingPreview({
  businessId,
  partnerId,
  partnerOnlinePurchPriceProfileId,
  enabled = true,
}: {
  businessId?: string;
  partnerId?: string;
  partnerOnlinePurchPriceProfileId?: string;
  enabled?: boolean;
}): OnlinePricingPreviewState {
  const [state, setState] = useState<OnlinePricingPreviewState>(() => {
    if (!enabled || !businessId) return { loading: false, failed: false };
    const cached = peekOnlinePricingPreview(
      previewParams(businessId, partnerId, partnerOnlinePurchPriceProfileId)
    );
    if (cached.hit) {
      return { loading: false, preview: cached.value, failed: false };
    }
    return { loading: true, failed: false };
  });
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!enabled || !businessId) {
      requestIdRef.current += 1;
      setState((prev) =>
        prev.loading || prev.preview || prev.failed
          ? { loading: false, failed: false }
          : prev
      );
      return;
    }
    const params = previewParams(
      businessId,
      partnerId,
      partnerOnlinePurchPriceProfileId
    );
    const cached = peekOnlinePricingPreview(params);
    if (cached.hit) {
      setState({ loading: false, preview: cached.value, failed: false });
      return;
    }
    const requestId = ++requestIdRef.current;
    setState({ loading: true, failed: false });
    loadOnlinePricingPreview(params)
      .then((preview) => {
        if (requestId !== requestIdRef.current) return;
        setState({ loading: false, preview, failed: false });
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;
        setState({
          loading: false,
          failed: true,
          failureReason: failureReason(error),
        });
      });
    return () => {
      requestIdRef.current += 1;
    };
  }, [businessId, partnerId, partnerOnlinePurchPriceProfileId, enabled]);

  return state;
}
